import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { ChevronDown, X, Search } from "lucide-react";

export interface SearchableSelectOption {
  value: string | number;
  label: string;
  /** Additional data to search in (e.g., address, tax_code) */
  searchData?: string;
  /** Any additional data to pass through */
  data?: Record<string, any>;
}

export interface SearchableSelectProps {
  /** Array of options to display */
  options: SearchableSelectOption[];
  /** Currently selected value */
  value?: string | number | null;
  /** Callback when selection changes */
  onChange?: (
    value: string | number | null,
    option: SearchableSelectOption | null,
  ) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Label for the field */
  label?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Custom class name */
  className?: string;
  /** Allow clearing selection */
  clearable?: boolean;
  /** Show search icon */
  showSearchIcon?: boolean;
  /** Error message */
  error?: string;
  /** Debounce search input (ms) */
  searchDebounce?: number;
  /** Max height of dropdown */
  maxDropdownHeight?: number;
  /** ID for testing */
  id?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  label,
  disabled = false,
  required = false,
  className = "",
  clearable = true,
  showSearchIcon = true,
  error,
  searchDebounce = 150,
  maxDropdownHeight = 280,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, searchDebounce);
    return () => clearTimeout(timer);
  }, [searchTerm, searchDebounce]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!debouncedSearch.trim()) return options;

    const search = debouncedSearch.toLowerCase().trim();
    return options.filter((opt) => {
      const labelMatch = opt.label.toLowerCase().includes(search);
      const valueMatch = String(opt.value).toLowerCase().includes(search);
      const dataMatch = opt.searchData?.toLowerCase().includes(search);
      return labelMatch || valueMatch || dataMatch;
    });
  }, [options, debouncedSearch]);

  // Find selected option
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;
    return options.find((opt) => String(opt.value) === String(value)) || null;
  }, [options, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      const focusedItem = items[focusedIndex] as HTMLElement;
      if (focusedItem) {
        focusedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setFocusedIndex(-1);
    // Focus search input after opening
    setTimeout(() => inputRef.current?.focus(), 10);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setFocusedIndex(-1);
  }, []);

  const handleSelect = useCallback(
    (option: SearchableSelectOption) => {
      onChange?.(option.value, option);
      handleClose();
    },
    [onChange, handleClose],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(null, null);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case "Enter":
          e.preventDefault();
          if (!isOpen) {
            handleOpen();
          } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
            handleSelect(filteredOptions[focusedIndex]);
          }
          break;

        case "Escape":
          e.preventDefault();
          handleClose();
          break;

        case "ArrowDown":
          e.preventDefault();
          if (!isOpen) {
            handleOpen();
          } else {
            setFocusedIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0,
            );
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          if (isOpen) {
            setFocusedIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1,
            );
          }
          break;

        case "Tab":
          if (isOpen) {
            handleClose();
          }
          break;
      }
    },
    [
      disabled,
      isOpen,
      focusedIndex,
      filteredOptions,
      handleOpen,
      handleClose,
      handleSelect,
    ],
  );

  const inputId =
    id || `searchable-select-${label?.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1.5 block text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {/* Trigger button */}
      <div
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${inputId}-listbox`}
        aria-disabled={disabled}
        className={`
          relative flex items-center w-full min-h-[42px] px-3 py-2
          border rounded-lg bg-white cursor-pointer
          transition-all duration-150
          ${
            isOpen
              ? "border-blue-500 ring-2 ring-blue-100"
              : error
                ? "border-red-400"
                : "border-gray-200 hover:border-gray-300"
          }
          ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : ""}
          focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100
        `}
      >
        <div className="flex-1 truncate text-sm">
          {selectedOption ? (
            <span className="text-gray-900">{selectedOption.label}</span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-1 ml-2">
          {clearable && selectedOption && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform duration-150 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{ maxHeight: maxDropdownHeight }}
        >
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              {showSearchIcon && (
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              )}
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type to search..."
                className={`
                  w-full py-2 text-sm border border-gray-200 rounded-md
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100
                  ${showSearchIcon ? "pl-9 pr-3" : "px-3"}
                `}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Options list */}
          <div
            ref={listRef}
            role="listbox"
            id={`${inputId}-listbox`}
            className="overflow-y-auto"
            style={{ maxHeight: maxDropdownHeight - 60 }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = selectedOption?.value === option.value;
                const isFocused = index === focusedIndex;

                return (
                  <div
                    key={`${option.value}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    className={`
                      px-3 py-2.5 cursor-pointer text-sm transition-colors
                      ${isFocused ? "bg-blue-50" : ""}
                      ${isSelected ? "bg-blue-100 text-blue-900 font-medium" : "text-gray-700"}
                      ${!isFocused && !isSelected ? "hover:bg-gray-50" : ""}
                    `}
                  >
                    {option.label}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {debouncedSearch
                  ? `No results for "${debouncedSearch}"`
                  : "No options available"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
